import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import PageHero from '@/components/PageHero';
import { getExhibitions } from '@/lib/api';
import PictureImg from '@/components/PictureImg';

const STATUS_ORDER = { current: 0, upcoming: 1, past: 2 };
const STATUS_LABEL = { current: 'On view', upcoming: 'Upcoming', past: 'Past' };
const fmt = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

export default function Exhibitions() {
    useDocumentMeta({
        title: 'Exhibitions · Maison Touré',
        description: 'Current, upcoming and past exhibitions at Maison Touré, Sydney — curated programmes of contemporary African art.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const hero = settings?.exhibitions_page || {};
    const [items, setItems] = useState([]);
    useEffect(() => {
        getExhibitions().then((d) => setItems([...d].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])));
    }, []);

    return (
        <div ref={reveal} data-testid="exhibitions-page" style={{ paddingTop: 144 }}>
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12 pb-40 md:pb-56">
                <PageHero
                    testid="exhibitions-hero"
                    overline={hero.hero_overline ?? 'The Programme'}
                    title={hero.hero_title ?? 'Exhibitions.'}
                    intro={hero.hero_intro ?? 'Curated presentations, built across years rather than seasons. Each exhibition is documented in print and online.'}
                />

                <div className="mt-32 md:mt-40 flex flex-col gap-32 md:gap-48">
                    {items.map((e, i) => (
                        <Link
                            to={`/exhibitions/${e.slug}`}
                            key={e.slug}
                            className={`reveal grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center group ${i % 2 ? 'md:[&>div:first-child]:order-2' : ''}`}
                            data-testid={`exhibition-card-${e.slug}`}
                        >
                            <div className="md:col-span-7">
                                <div className="img-wrap aspect-[5/4]" style={{ background: 'var(--bg-3)' }}>
                                    <PictureImg src={e.cover_image} alt={e.title} loading="lazy" />
                                </div>
                            </div>
                            <div className="md:col-span-4 md:col-start-9">
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>
                                    {STATUS_LABEL[e.status]}
                                </span>
                                <h2
                                    className="mt-6"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 'clamp(40px, 4.6vw, 72px)',
                                        lineHeight: 1.04,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    {e.title}
                                </h2>
                                <div
                                    className="mt-3"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontSize: 18,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {e.subtitle}
                                </div>
                                <div
                                    className="mt-8 overline"
                                    style={{ color: 'var(--ink-3)' }}
                                >
                                    {fmt(e.start_date)} — {fmt(e.end_date)}
                                </div>
                                <span className="btn-ghost mt-10 inline-flex">View exhibition</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
